import { callBackend } from './api';

function stopped() {
    const error = new Error('Optimization stopped.');
    error.name = 'AbortError';
    return error;
}

function pause(milliseconds, signal) {
    return new Promise((resolve, reject) => {
        const cancel = () => { clearTimeout(timer); reject(stopped()); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort', cancel); resolve(); }, milliseconds);
        signal?.addEventListener('abort', cancel, { once: true });
        if (signal?.aborted) cancel();
    });
}

// Retry only an explicit admission response, always with the original operation
// id and canonical command. A network error is not permission to start a solve.
export async function runOptimizerWithQueue(command, {
    isCurrent = () => true, onWaiting = () => {}, signal, request = callBackend,
    wait = pause, now = Date.now,
    endpoint = 'run_portfolio_optimization_v2',
} = {}) {
    const deadline = now() + 10 * 60 * 1000;
    let complete = false;
    let operationId = command.run_operation_id;
    try {
        while (isCurrent() && !signal?.aborted) {
            onWaiting(null);
            const result = await request(endpoint, 'POST', command, ...(signal ? [{ signal }] : []));
            if (result?.code !== 'optimizer_waiting' || result.status !== 'waiting') {
                complete = true;
                return result;
            }
            operationId = result.operation_id || operationId;
            if (now() >= deadline) throw new Error('The optimizer is still busy. Please try again shortly.');
            onWaiting(result);
            const seconds = Math.max(2, Math.min(15, Number(result.retry_after_seconds) || 5));
            await wait(seconds * 1000, signal);
        }
        throw stopped();
    } finally {
        onWaiting(null);
        if (!complete && operationId && (signal?.aborted || !isCurrent() || now() >= deadline)) {
            // This cancels queued work or requests cancellation of a live run.
            // The backend retains its slot until the solver actually returns.
            await request(`optimizer_runs/${encodeURIComponent(operationId)}`, 'DELETE').catch(() => {});
        }
    }
}
